import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { Collection, type Name } from '../collection'

import type { TypeParams } from './type-params'

/**
 * Produces an entity collection observable based on the given producer.
 */
// TODO: Maybe we can add more options observable, callback for entity
// collection name, callback for observable
export type CollectionProducer<
  TTypeParams extends TypeParams.Constraint = TypeParams.Constraint,
> = Name | Collection<TTypeParams> | (() => Collection<TTypeParams>)
export namespace CollectionProducer {
  /**
   * Generates an Observable for the specified {@link CollectionProducer},
   * yielding an entity collection.
   */
  export function getEc$<
    TTypeParams extends TypeParams.Constraint = TypeParams.Constraint,
  >(
    producer: CollectionProducer<TTypeParams>,
    {
      waitUntilReady = false,
    }: {
      /**
       * Waits until the entity collection is ready
       */
      readonly waitUntilReady?: boolean
    } = {},
  ): rxjs.Observable<Collection<TTypeParams>> {
    let collection$ =
      typeof producer === 'string'
        ? Collection.instances.get<TTypeParams>(producer).asObservable
        : rxjs.of(TypeChecker.isFunction(producer) ? producer() : producer)

    // Usually we are not interested in the collection emission after the first
    // emission
    collection$ = collection$.pipe(rxjs.take(1))

    if (waitUntilReady) {
      collection$ = collection$.pipe(
        rxjs.switchMap(collection =>
          collection.ready$.pipe(rxjs.map(() => collection)),
        ),
      )
    }

    return collection$
  }

  /**
   * Returns the {@link Name} of the {@link Collection} produced by the given
   * {@link CollectionProducer}
   */
  export function getName<
    TTypeParams extends TypeParams.Constraint = TypeParams.Constraint,
  >(producer: CollectionProducer<TTypeParams>): Name {
    if (typeof producer === 'string') {
      return producer
    }
    if (producer instanceof Collection) {
      return producer.name
    }
    return producer().name
  }
}
